<template>
    <div>
        <portal :to="`official-results-controls-${round}`">
            <template v-if="verifyingResults">
                <div v-if="markOfficialValidationErrors">
                    <v-alert type="error" dismissible>
                        <div v-for="(error, field) in markOfficialValidationErrors" :key="field">
                            <strong>{{ field }}</strong>
                            <span>:</span>
                            <span>{{ error.join(', ') }}</span>
                        </div>
                    </v-alert>
                </div>

                <p>
                    <span>
                        Select the checkboxes to mark results as official. Once submitted, an ‘official’ badge (
                    </span>
                    <span><v-icon x-small>mdi-check-decagram</v-icon></span>
                    <span>) will appear next to the results, indicating they have been reviewed and finalized.</span>
                </p>

                <div>
                    <!-- Mark official button -->
                    <v-btn
                        :disabled="resultsToMarkOfficial.length === 0 && officialResultCount === 0"
                        :loading="markingOfficial"
                        color="primary"
                        class="mr-2"
                        @click="toggleOfficialStatus(resultsToMarkOfficial)"
                    >
                        <v-icon class="pr-1">mdi-check-decagram</v-icon>
                        Submit
                    </v-btn>

                    <!-- Cancel button -->
                    <v-btn @click="verifyingResults = false">
                        <v-icon class="pr-1">mdi-close</v-icon>
                        Cancel
                    </v-btn>
                </div>

                <div class="mt-2">
                    <!-- Check all button -->
                    <v-btn
                        :disabled="resultsToMarkOfficial.length === resultsForRound.length"
                        color="primary"
                        class="mr-2"
                        small
                        @click="resultsToMarkOfficial = resultsForRound.map((result) => result.id)"
                    >
                        <v-icon class="pr-1">mdi-check-all</v-icon>
                        Check All
                    </v-btn>

                    <!-- Uncheck all button -->
                    <v-btn
                        :disabled="resultsToMarkOfficial.length === 0"
                        color="primary"
                        class="mr-2"
                        small
                        @click="resultsToMarkOfficial = []"
                    >
                        <v-icon class="pr-1">mdi-close</v-icon>
                        Uncheck All
                    </v-btn>
                </div>
            </template>
        </portal>

        <v-data-table
            v-if="$vuetify.breakpoint.smAndUp"
            ref="results"
            :loading="loading"
            :headers="headers"
            :items="resultsForRound"
            :search="search"
            :show-expand="hasSkillDeductions"
            :expanded.sync="expanded"
            :items-per-page="-1"
            class="elevation-1"
            :class="[hasSkillDeductions ? 'clickable' : '']"
            hide-default-footer
            disable-pagination
            disable-sort
            item-class="highlighted"
            no-data-text="No results yet."
            no-results-text="No results matching your search criteria."
            @click:row="rowClicked"
        >
            <template #[`item.marked_official_at`]="{ item }">
                <template v-if="verifyingResults">
                    <v-checkbox v-model="resultsToMarkOfficial" :value="item.id" color="primary" @click.stop />
                </template>
                <template v-else>
                    <v-icon v-if="item[`${round}_marked_official_at`]">mdi-check-decagram</v-icon>
                </template>
            </template>

            <template #[`item.prelims_placement`]="{ item }">
                <result-placement
                    :id="`result-${item.id}`"
                    :result="item"
                    :meet="meet"
                    round="prelims"
                    @more-info="$emit('more-info')"
                />
            </template>

            <template #[`item.finals_placement`]="{ item }">
                <result-placement
                    :id="`result-${item.id}`"
                    :result="item"
                    :meet="meet"
                    round="finals"
                    @more-info="$emit('more-info')"
                />
            </template>

            <template #[`item.semi_final_placement`]="{ item }">
                <result-placement
                    :id="`result-${item.id}`"
                    :result="item"
                    :meet="meet"
                    round="semi_final"
                    @more-info="$emit('more-info')"
                />
            </template>

            <template #[`item.final_one_placement`]="{ item }">
                <result-placement
                    :id="`result-${item.id}`"
                    :result="item"
                    :meet="meet"
                    round="final_one"
                    @more-info="$emit('more-info')"
                />
            </template>

            <template #[`item.final_two_placement`]="{ item }">
                <result-placement
                    :id="`result-${item.id}`"
                    :result="item"
                    :meet="meet"
                    round="final_two"
                    @more-info="$emit('more-info')"
                />
            </template>

            <template #[`item.qualification`]="{ item }">
                <template v-if="editingQualification">
                    <v-select
                        :value="item[`${round}_qual`]"
                        :items="qualifications"
                        item-text="text"
                        item-value="value"
                        @change="(value) => item.$update({ [`${round}_qual`]: value })"
                    />
                </template>

                <template v-else>{{ item[`${round}_qual`] }}</template>
            </template>

            <template #[`item.share`]="{ item }">
                <scorsync-share :result="item" :round="round" />
            </template>

            <template #[`item.video`]="{ item }">
                <div class="text-center mx-auto">
                    <div v-for="routine in item.routineResultsForRound(round)" :key="routine.id">
                        <scorsync-video
                            :key="`video-${routine.id}`"
                            :routine="routine"
                            :result="item"
                            :result-category="resultCategory"
                            :round="round"
                            @opened="videoOpen = true"
                            @closed="videoOpen = false"
                        />
                    </div>
                </div>
            </template>

            <template #[`item.image`]="{ item }">
                <athlete-avatar :person="item.athlete" />
            </template>

            <template #[`item.synchro_images`]="{ item }">
                <div>
                    <athlete-avatar :person="item.athlete" />
                </div>
                <div>
                    <athlete-avatar :person="item.athlete2" />
                </div>
            </template>

            <template #[`item.athlete.name`]="{ item }">
                <inertia-link :href="`/athletes/${item.athlete.id}`">
                    {{ item.athlete.name }}
                    <span v-if="item.athlete.is_birthday" role="img" aria-label="Birthday cake">🎂</span>
                </inertia-link>
            </template>

            <template #[`item.synchro_pair`]="{ item }">
                <div>
                    <inertia-link :href="`/athletes/${item.athlete.id}`">
                        {{ item.athlete.name }}
                    </inertia-link>
                </div>

                <div>
                    <inertia-link :href="`/athletes/${item.athlete2.id}`">
                        {{ item.athlete2.name }}
                    </inertia-link>
                </div>
            </template>

            <template #[`item.club.name`]="{ item }">
                <country-flag
                    v-if="meet.is_international && item.club.country"
                    :country="item.club.country"
                    width="20"
                />
                <inertia-link :href="`/clubs/${item.club.id}`">
                    {{ item.club.name }}
                </inertia-link>
            </template>

            <template #[`item.synchro_clubs`]="{ item }">
                <country-flag
                    v-if="meet.is_international && item.club.country"
                    :country="item.club.country"
                    width="20"
                    class="elevation-1"
                />
                <div>
                    <inertia-link :href="`/clubs/${item.club.id}`">
                        {{ item.club.name }}
                    </inertia-link>
                </div>

                <div v-if="!meet.is_international">
                    <inertia-link :href="`/clubs/${item.club2.id}`">
                        {{ item.club2.name }}
                    </inertia-link>
                </div>
            </template>

            <template #[`item.num`]="{ item }">
                <div v-for="routine in item.routineResultsForRound(round)" :key="routine.id">
                    {{ routine.num }}
                </div>
            </template>

            <!-- Unused -->
            <template #[`item.e1`]="{ item }">
                <div v-for="routine in item.routineResultsForRound(round)" :key="routine.id">
                    <score-component :routine="routine" :threshold="thresholdForChanges" component="e1" />
                </div>
            </template>

            <!-- Unused -->
            <template #[`item.e2`]="{ item }">
                <div v-for="routine in item.routineResultsForRound(round)" :key="routine.id">
                    <score-component :routine="routine" :threshold="thresholdForChanges" component="e2" />
                </div>
            </template>

            <!-- Unused -->
            <template #[`item.e3`]="{ item }">
                <div v-for="routine in item.routineResultsForRound(round)" :key="routine.id">
                    <score-component :routine="routine" :threshold="thresholdForChanges" component="e3" />
                </div>
            </template>

            <!-- Unused -->
            <template #[`item.e4`]="{ item }">
                <div v-for="routine in item.routineResultsForRound(round)" :key="routine.id">
                    <score-component :routine="routine" :threshold="thresholdForChanges" component="e4" />
                </div>
            </template>

            <template #[`item.execution`]="{ item }">
                <div v-for="routine in item.routineResultsForRound(round)" :key="routine.id">
                    <score-component :routine="routine" :threshold="thresholdForChanges" component="execution" />
                </div>
            </template>

            <template #[`item.hd`]="{ item }">
                <div v-for="routine in item.routineResultsForRound(round)" :key="routine.id">
                    <score-component :routine="routine" :threshold="thresholdForChanges" component="hd" />
                </div>
            </template>

            <template #[`item.hd1`]="{ item }">
                <div v-for="routine in item.routineResultsForRound(round)" :key="routine.id">
                    <score-component :routine="routine" :threshold="thresholdForChanges" component="hd1" />
                </div>
            </template>

            <template #[`item.hd2`]="{ item }">
                <div v-for="routine in item.routineResultsForRound(round)" :key="routine.id">
                    <score-component :routine="routine" :threshold="thresholdForChanges" component="hd2" />
                </div>
            </template>

            <template #[`item.sync1`]="{ item }">
                <div v-for="routine in item.routineResultsForRound(round)" :key="routine.id">
                    <score-component :routine="routine" :threshold="thresholdForChanges" component="sync1" />
                </div>
            </template>

            <template #[`item.sync2`]="{ item }">
                <div v-for="routine in item.routineResultsForRound(round)" :key="routine.id">
                    <score-component :routine="routine" :threshold="thresholdForChanges" component="sync2" />
                </div>
            </template>

            <template #[`item.sync`]="{ item }">
                <div v-for="routine in item.routineResultsForRound(round)" :key="routine.id">
                    <score-component :routine="routine" :threshold="thresholdForChanges" component="sync" />
                </div>
            </template>

            <template #[`item.tof`]="{ item }">
                <div v-for="routine in item.routineResultsForRound(round)" :key="routine.id">
                    <score-component :routine="routine" :threshold="thresholdForChanges" component="tof" />
                </div>
            </template>

            <template #[`item.dd`]="{ item }">
                <div v-for="routine in item.routineResultsForRound(round)" :key="routine.id">
                    <score-component :routine="routine" :threshold="thresholdForChanges" component="dd" />
                </div>
            </template>

            <template #[`item.nd`]="{ item }">
                <div v-for="routine in item.routineResultsForRound(round)" :key="routine.id">
                    <score-component :routine="routine" :threshold="thresholdForChanges" component="nd" />
                </div>
            </template>

            <template #[`item.trampoline_score`]="{ item }">
                <inertia-link v-if="item.tra_result" :href="item.tra_result.highlighted_route">
                    {{ numeral(item.tra_result.prelims_score).format('0.000') }}
                </inertia-link>
                <span v-else>0.000</span>
            </template>

            <template #[`item.double_mini_score`]="{ item }">
                <inertia-link v-if="item.dmt_result" :href="item.dmt_result.highlighted_route">
                    {{ numeral(item.dmt_result.prelims_score).format('0.000') }}
                </inertia-link>
                <span v-else>0.000</span>
            </template>

            <template #[`item.tumbling_score`]="{ item }">
                <inertia-link v-if="item.tum_result" :href="item.tum_result.highlighted_route">
                    {{ numeral(item.tum_result.prelims_score).format('0.000') }}
                </inertia-link>
                <span v-else>0.000</span>
            </template>

            <template #[`item.score`]="{ item }">
                <div v-for="routine in item.routineResultsForRound(round)" :key="routine.id">
                    <!-- if the routine changed, add a tooltip to highlight what changed -->
                    <score-component :routine="routine" :threshold="thresholdForChanges" component="total">
                        <template #tooltip>
                            Previous Score<br />
                            <div v-for="change in routine.changedFields(thresholdForChanges) || []" :key="change">
                                {{ change.toUpperCase() }}:
                                <span class="font-weight-bold">
                                    {{ routine.changed(thresholdForChanges).old[change] }}
                                </span>
                            </div>
                        </template>
                    </score-component>
                </div>
            </template>

            <template #[`item.prelims_score`]="{ item }">
                <strong
                    :class="{
                        'yellow--text': item.changed('prelims', thresholdForChanges),
                        'text--darken-2': item.changed('prelims', thresholdForChanges) && $vuetify.theme.dark,
                        'text--darken-4': item.changed('prelims', thresholdForChanges) && !$vuetify.theme.dark,
                    }"
                >
                    {{ numeral(item.prelims_score).format('0.000') }}
                </strong>
            </template>

            <template #[`item.semi_final_score`]="{ item }">
                <strong
                    :class="{
                        'yellow--text': item.changed('semi_final', thresholdForChanges),
                        'text--darken-2': item.changed('semi_final', thresholdForChanges) && $vuetify.theme.dark,
                        'text--darken-4': item.changed('semi_final', thresholdForChanges) && !$vuetify.theme.dark,
                    }"
                >
                    {{ numeral(item.semi_final_score).format('0.000') }}
                </strong>
            </template>

            <template #[`item.finals_score`]="{ item }">
                <strong
                    :class="{
                        'yellow--text': item.changed('finals', thresholdForChanges),
                        'text--darken-2': item.changed('finals', thresholdForChanges) && $vuetify.theme.dark,
                        'text--darken-4': item.changed('finals', thresholdForChanges) && !$vuetify.theme.dark,
                    }"
                >
                    {{ numeral(item.finals_score).format('0.000') }}
                </strong>
            </template>

            <template #[`item.final_one_score`]="{ item }">
                <strong
                    :class="{
                        'yellow--text': item.changed('final_one', thresholdForChanges),
                        'text--darken-2': item.changed('final_one', thresholdForChanges) && $vuetify.theme.dark,
                        'text--darken-4': item.changed('final_one', thresholdForChanges) && !$vuetify.theme.dark,
                    }"
                >
                    {{ numeral(item.final_one_score).format('0.000') }}
                </strong>
            </template>

            <template #[`item.final_two_score`]="{ item }">
                <strong
                    :class="{
                        'yellow--text': item.changed('final_two', thresholdForChanges),
                        'text--darken-2': item.changed('final_two', thresholdForChanges) && $vuetify.theme.dark,
                        'text--darken-4': item.changed('final_two', thresholdForChanges) && !$vuetify.theme.dark,
                    }"
                >
                    {{ numeral(item.final_two_score).format('0.000') }}
                </strong>
            </template>

            <template #[`item.total_score`]="{ item }">
                <strong
                    :class="{
                        'yellow--text': item.changed('finals', thresholdForChanges),
                        'text--darken-2': item.changed('finals', thresholdForChanges) && $vuetify.theme.dark,
                        'text--darken-4': item.changed('finals', thresholdForChanges) && !$vuetify.theme.dark,
                    }"
                >
                    {{ numeral(item.total_score).format('0.000') }}
                </strong>
            </template>

            <template v-if="canQualify" #[`item.qualified`]="{ item }">
                <v-icon v-if="item.qualified" color="green">check</v-icon>
                <v-icon v-else color="grey lighten-2">mdi-close</v-icon>
            </template>

            <template v-if="canMobilize" #[`item.mobilized`]="{ item }">
                <!--                <v-icon v-if="item.mobilized" color="green">check</v-icon>-->
                <!--                <v-icon v-else color="grey lighten-2">mdi-close</v-icon>-->
                <mobility-check
                    :result="item"
                    :mobility="mobilityFor(item)"
                    :meet="meet"
                    bg-failed="white"
                    fg-failed="black"
                />
            </template>

            <template v-if="teamQualifiers.length > 0" #[`item.wc-preferential`]="{ item }">
                <v-icon v-if="selectionScoreHit(item, 'world-championships', 'preferential')" color="green">
                    check
                </v-icon>
                <v-icon v-else color="grey lighten-2">mdi-close</v-icon>
            </template>

            <template v-if="teamQualifiers.length > 0" #[`item.wc-minimum`]="{ item }">
                <v-icon v-if="selectionScoreHit(item, 'world-championships', 'minimum')" color="green">check</v-icon>
                <v-icon v-else color="grey lighten-2">mdi-close</v-icon>
            </template>

            <template v-if="teamQualifiers.length > 0" #[`item.wagc-preferential`]="{ item }">
                <v-icon v-if="selectionScoreHit(item, 'world-age-group-championships', 'preferential')" color="green">
                    check
                </v-icon>
                <v-icon v-else color="grey lighten-2">mdi-close</v-icon>
            </template>

            <template v-if="teamQualifiers.length > 0" #[`item.wagc-minimum`]="{ item }">
                <v-icon v-if="selectionScoreHit(item, 'world-age-group-championships', 'minimum')" color="green">
                    check
                </v-icon>
                <v-icon v-else color="grey lighten-2">mdi-close</v-icon>
            </template>

            <template v-if="teamQualifiers.length > 0" #[`item.sr-national-team-minimum`]="{ item }">
                <v-icon v-if="selectionScoreHit(item, 'senior-national-team', 'minimum')" color="green">check</v-icon>
                <v-icon v-else color="grey lighten-2">mdi-close</v-icon>
            </template>

            <template v-if="teamQualifiers.length > 0" #[`item.jr-national-team-minimum`]="{ item }">
                <v-icon v-if="selectionScoreHit(item, 'junior-national-team', 'minimum')" color="green">check</v-icon>
                <v-icon v-else color="grey lighten-2">mdi-close</v-icon>
            </template>

            <template v-if="isAdmin" #[`item.admin`]="{ item }">
                <a :href="`/nova/resources/results/${item.id}`" target="_blank">
                    <v-icon>launch</v-icon>
                </a>
            </template>

            <template #[`item.id`]="{ item }">
                <a :href="`/nova/resources/results/${item.id}`" target="_blank"> #{{ item.id }} </a>
            </template>

            <template #[`item.routine_id`]="{ item }">
                <div v-for="routine in item.routineResultsForRound(round)" :key="routine.id">
                    <a :href="`/nova/resources/${routine.nova_resource}/${routine.id}`" target="_blank">
                        #{{ routine.id }}
                    </a>
                </div>
            </template>

            <template #expanded-item="{ headers: colHeaders, item }">
                <td :colspan="colHeaders.length - 4">
                    <div
                        v-for="routine in item.routine_results.filter((routine) => routine.round === round)"
                        :key="`routine-${routine.id}`"
                    >
                        <p class="font-weight-bold mt-4 mb-0 ml-4">Skill deductions for routine #{{ routine.num }}</p>

                        <v-simple-table v-if="routineSkillDeductions(routine).length > 0" dense class="mb-2">
                            <template #default>
                                <thead>
                                    <tr>
                                        <th>POS</th>
                                        <th v-for="skill in numSkills" :key="skill">S{{ skill }}</th>
                                        <th>LAN</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="skillDeductions in routineSkillDeductions(routine).filter(
                                            (sd) => sd.position !== 'D1',
                                        )"
                                        :key="`skill-deductions-${skillDeductions.id}`"
                                    >
                                        <td>{{ skillDeductions.position }}</td>
                                        <td v-for="skill in numSkills" :key="skill" class="font-weight-bold">
                                            {{ skillDeductions[`s${skill}`] }}
                                        </td>
                                        <td class="font-weight-bold">{{ skillDeductions.landing }}</td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-simple-table>

                        <p v-else class="font-italic ml-4 grey--text">
                            No skill deductions available for this routine.
                        </p>
                    </div>
                </td>
            </template>
        </v-data-table>

        <template v-else>
            <result-card
                v-for="result in resultsForRound"
                :id="`result-${result.id}`"
                :key="`secondary-${result.id}`"
                :result="result"
                :round="round"
                :meet="meet"
                :mobilities="mobilities"
                :result-category="resultCategory"
                :can-mobilize="canMobilize"
                :can-qualify="canQualify"
                :team-qualifiers="teamQualifiers"
                :is-a-final-round="['semi_final', 'finals', 'final_one', 'final_two'].includes(round)"
                :is-cumulative="isCumulative"
                :threshold-for-changes="thresholdForChanges"
                :verifying-results="verifyingResults"
                :results-to-mark-official="resultsToMarkOfficial"
                @video-opened="videoOpen = true"
                @video-closed="videoOpen = false"
                @changed-official="handleOfficialChanged"
            />
        </template>

        <inertia-link
            as="v-btn"
            :href="`/meets/${meet.slug}`"
            fab
            dark
            small
            color="primary"
            fixed
            left
            bottom
            :elevation="2"
        >
            <v-icon dark>mdi-arrow-left</v-icon>
        </inertia-link>
    </div>
</template>

<script>
import numeral from 'numeral';
import { VBtn } from 'vuetify/lib/components';
import Result from '@/Models/Result.js';
import Meet from '@/Models/Meet.js';
import AthleteAvatar from '@/Components/ScorsyncAvatar.vue';
import ResultsMixin from '@/Components/Results/Results.mixin.js';
import Video from '@/Models/Video.js';
import ScorsyncVideo from '@/Components/ScorsyncVideo.vue';
import ScorsyncShare from '@/Components/ScorsyncShare.vue';
import CountryFlag from '@/Components/CountryFlag.vue';
import User from '@/Models/User.js';
import MobilityCheck from '@/Components/MobilityCheck.vue';
import ResultPlacement from '@/Components/Results/ResultPlacement.vue';
import ResultCard from '@/Components/Results/ResultCard.vue';
import ScoreComponent from '@/Components/Results/ScoreComponent.vue';
import AllAroundResult from '@/Models/AllAroundResult.js';

export default {
    name: 'RoundResults',

    components: {
        ScoreComponent,
        ResultCard,
        ResultPlacement,
        CountryFlag,
        ScorsyncShare,
        ScorsyncVideo,
        AthleteAvatar,
        // eslint-disable-next-line vue/no-unused-components
        VBtn,
        MobilityCheck,
    },

    mixins: [ResultsMixin],

    props: {
        teamQualifiers: {
            type: Array,
            required: false,
            default: () => [],
        },

        editingQualification: {
            type: Boolean,
            required: false,
            default: false,
        },

        mobilities: {
            type: Array,
            required: false,
            default: () => [],
        },

        loading: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    data: () => ({
        search: null,
        qualifications: [
            { value: null, text: 'N/A' },
            { value: 'Q', text: 'Qualified' },
            { value: 'R1', text: '1st Reserve' },
            { value: 'R2', text: '2nd Reserve' },
            { value: 'R3', text: '3rd Reserve' },
        ],
        expanded: [],
        skillDeductionSort: { E1: 1, E2: 2, E3: 3, E4: 4, E5: 5, E6: 6, HD1: 7, HD2: 8, DD: 9 },
        videoOpen: false,
        thresholdForChanges: 60,
        resultsToMarkOfficial: [],
        markingOfficial: false,
        markOfficialValidationErrors: null,
    }),

    computed: {
        user() {
            return User.query().withAll().find(this.$page.props.auth.user?.id);
        },

        headers() {
            let columns = [];

            if (this.debugMode) {
                columns.push({
                    text: 'ID',
                    value: 'id',
                });
            }

            columns.push({
                text: 'Official',
                value: 'marked_official_at',
            });

            columns.push({
                text: 'Rank',
                value: `${this.round}_placement`,
            });

            // TODO: Consider including this for all meets
            if (this.meet.is_international && !['finals', 'final_two'].includes(this.round)) {
                columns.push({
                    text: 'Qual',
                    value: 'qualification',
                });
            }

            columns = columns.concat(this.resultHeaders);

            if (this.isAFinalRound && this.isCumulative) {
                columns.push({
                    text: 'Total',
                    value: 'total_score',
                });
            } else {
                columns.push({
                    text: 'Total',
                    value: `${this.round}_score`,
                });
            }

            if (this.canQualify && this.round === 'prelims') {
                columns.push({
                    text: 'Qualified',
                    value: 'qualified',
                });
            }

            if (this.canMobilize && this.round === 'prelims') {
                columns.push({
                    text: 'Hit Mobility',
                    value: 'mobilized',
                });
            }

            if (this.teamQualifiers.length > 0) {
                this.pushSelectionScoreColumns(columns);
            }

            if (this.isAdmin) {
                columns.push({
                    text: 'Admin',
                    value: 'admin',
                });
            }

            if (this.resultCategory.event !== 'AA') {
                columns.push({
                    text: 'Video',
                    value: 'video',
                });
            }

            // columns.push({
            //     text: 'Share',
            //     value: 'share',
            // });


            return columns;
        },

        canQualify() {
            if (this.resultCategory.event === 'AA') {
                return false;
            }

            if (this.resultCategory.is_elite) {
                return this.meet.elite_qualification_meet;
            }

            return this.meet.jo_qualification_meet && !['1', '2', '3', '4'].includes(this.resultCategory.level);
        },

        canMobilize() {
            if (this.resultCategory.event === 'AA') {
                return false;
            }

            if (['1', '2', '3', '4'].includes(this.resultCategory.level) || this.resultCategory.event === 'TS') {
                return false;
            }

            if (this.resultCategory.is_elite) {
                return this.meet.elite_mobility && this.resultCategory.level !== 'SR';
            }

            return true;
        },

        videos() {
            return Video.query()
                .where('processed', 0)
                .where('created_at', (createdAt) => {
                    return this.$moment().subtract('2', 'hours').isBefore(this.$moment.utc(createdAt));
                })
                .get();
        },

        debugMode() {
            return this.$store.state.debugMode;
        },

        hasSkillDeductions() {
            return (
                this.resultsForRound.filter((result) => {
                    if (result instanceof AllAroundResult) {
                        return false;
                    }
                    return (
                        result.routine_results.filter((routine) => {
                            return (
                                routine.skill_deductions.filter((skillDeduction) => {
                                    return skillDeduction.s1 !== null;
                                }).length > 0
                            );
                        }).length > 0
                    );
                }).length > 0
            );
        },

        numSkills() {
            return {
                TI: 10,
                DM: 2,
                TU: 8,
                TS: 10,
            }[this.resultCategory.event];
        },

        verifyingResults: {
            get() {
                return this.$store.state.entities.meets.verifyingResults === this.meet.id;
            },

            set(value) {
                Meet.commit((state) => {
                    state.verifyingResults = value ? this.meet.id : null;
                });
            },
        },

        officialResultCount() {
            return this.resultsForRound.filter((result) => result[`${this.round}_marked_official_at`] !== null).length;
        },
    },

    watch: {
        round: {
            immediate: true,
            handler() {
                this.resetResultsToMarkOfficial();
            },
        },
    },

    created() {
        this.videos.forEach((video) => {
            this.$echo.channel(`video-${video.uuid}`).listen('VideoProcessed', (e) => {
                Video.insert({ data: e.video });

                this.$echo.leave(`video-${video.uuid}`);
            });
        });
    },

    beforeDestroy() {
        this.videos.forEach((video) => {
            this.$echo.leave(`video-${video.uuid}`);
        });
    },

    mounted() {
        if (this.$refs.results) {
            const tbody = this.$refs.results.$el.getElementsByTagName('tbody')[0];
            tbody.setAttribute('name', 'fade');
            tbody.setAttribute('is', 'transition-group');

            const rows = this.$refs.results.$el.getElementsByTagName('tr');
            const rowsArray = Array.from(rows);

            Object.keys(rowsArray).forEach((key) => {
                rowsArray[key].style.display = 'table-row';
            });
        }
    },

    methods: {
        numeral,

        pushSelectionScoreColumns(columns) {
            this.teamQualifiers.forEach((teamQualifier) => {
                if (teamQualifier.key.endsWith('world-championships')) {
                    columns.push({
                        text: 'WC Preferential',
                        value: 'wc-preferential',
                    });

                    columns.push({
                        text: 'WC Minimum',
                        value: 'wc-minimum',
                    });
                } else if (teamQualifier.key.endsWith('world-age-group-championships')) {
                    columns.push({
                        text: 'WAGC Preferential',
                        value: 'wagc-preferential',
                    });

                    columns.push({
                        text: 'WAGC Minimum',
                        value: 'wagc-minimum',
                    });
                } else if (teamQualifier.key.endsWith('senior-national-team')) {
                    columns.push({
                        text: 'SR NT Minimum',
                        value: 'sr-national-team-minimum',
                    });
                } else if (teamQualifier.key.endsWith('jr-national-team')) {
                    columns.push({
                        text: 'JR NT Minimum',
                        value: 'jr-national-team-minimum',
                    });
                }
            });
        },

        selectionScoreHit(result, teamQualifierKey, scoreKey) {
            const qualifier = result.selection_score_report?.find((selectionScoreReport) =>
                selectionScoreReport.teamQualifier.key.endsWith(teamQualifierKey),
            );

            return qualifier && qualifier.report[`${scoreKey}ScoreHit`];
        },

        routineSkillDeductions(routine) {
            return _.clone(routine.skill_deductions).sort(
                (a, b) => this.skillDeductionSort[a.position] - this.skillDeductionSort[b.position],
            );
        },

        rowClicked(row, props) {
            if (this.hasSkillDeductions) {
                props.expand(!props.isExpanded);
            }
        },

        mobilityFor(result) {
            // Adjust this because mobility.min_age and mobility.max_age can be null
            return this.mobilities.filter((mobility) => {
                if (mobility.min_age === null && mobility.max_age === null) return true;
                if (mobility.min_age === null) return result.athlete.age <= mobility.max_age;
                if (mobility.max_age === null) return result.athlete.age >= mobility.min_age;
                return result.athlete.age >= mobility.min_age && result.athlete.age <= mobility.max_age;
            });
        },

        async toggleOfficialStatus(resultIds) {
            this.markingOfficial = true;
            this.markOfficialValidationErrors = null;

            try {
                await Result.api().put('/api/results/mark-official', {
                    round: this.round,
                    official: resultIds,
                    unofficial: this.resultsForRound
                        .filter((result) => !resultIds.includes(result.id))
                        .map((result) => result.id),
                });

                Meet.commit((state) => (state.verifyingResults = false));
            } catch (e) {
                this.markOfficialValidationErrors = e.response.data.errors;
            } finally {
                this.markingOfficial = false;
            }
        },

        handleOfficialChanged(result, isOfficial) {
            if (isOfficial) {
                this.resultsToMarkOfficial.push(result.id);
            } else {
                this.resultsToMarkOfficial = this.resultsToMarkOfficial.filter((id) => id !== result.id);
            }
        },

        resetResultsToMarkOfficial() {
            this.resultsToMarkOfficial = this.resultsForRound
                .filter((result) => result[`${this.round}_marked_official_at`] !== null)
                .map((result) => result.id);
        },
    },
};
</script>

<style lang="scss">
.fade-enter-active,
.fade-leave-active {
    transition: all 1s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
}

.score-table td {
    padding-left: 0.25rem !important;
    padding-right: 0.25rem !important;
}

.score-table th {
    padding-left: 0.25rem !important;
    padding-right: 0.25rem !important;
}

.text-truncated {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.clickable td {
    cursor: pointer;
}

.highlighted {
    background-color: rgba(24, 72, 229, 0.3) !important;
}
</style>
